import React, { useEffect, useState } from "react";

export default function AdminUpcommingEventsDialog({ open, onClose, onSave, initial = null, isSaving = false }) {
  const [form, setForm] = useState({ title: "", date: "", startTime: "", endTime: "", location: "", description: "", category: "", registrationLink: "", featured: false, active: false, image: null });
  const [linkError, setLinkError] = useState("");
  const [isCheckingLink, setIsCheckingLink] = useState(false);

  useEffect(() => {
    if (initial) {
      const pad = (n) => String(n).padStart(2, '0');
      const toLocalDate = (d) => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
      const toLocalTime = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

      // Attempt to read explicit startDateTime/endDateTime from initial
      let dateVal = "";
      let sTime = "";
      let eTime = "";
      try {
        const sRaw = initial.startDateTime || initial.start_date_time || initial.startDateTime || initial.start;
        if (sRaw) {
          const sd = sRaw instanceof Date ? sRaw : new Date(sRaw);
          if (!isNaN(sd.getTime())) {
            dateVal = toLocalDate(sd);
            sTime = toLocalTime(sd);
          }
        }
        const eRaw = initial.endDateTime || initial.end_date_time || initial.endDateTime || initial.end;
        if (eRaw) {
          const ed = eRaw instanceof Date ? eRaw : new Date(eRaw);
          if (!isNaN(ed.getTime())) {
            if (!dateVal) dateVal = toLocalDate(ed);
            eTime = toLocalTime(ed);
          }
        }
      } catch (err) {
        // fall through to other shapes below
      }

      // If times weren't derived from start/end datetimes, fall back to legacy fields
      if (!sTime) sTime = (initial.startTime || (initial.time && initial.time.start) || (initial.eventTime && initial.eventTime.start)) || "";
      if (!eTime) eTime = (initial.endTime || (initial.time && initial.time.end) || (initial.eventTime && initial.eventTime.end)) || "";

      // If date not set yet, try legacy date field parsing
      if (!dateVal) {
        const d = initial.date;
        if (!d) dateVal = "";
        else if (d instanceof Date) dateVal = d.toISOString().slice(0,10);
        else {
          const n = Number(d);
          if (!Number.isNaN(n)) {
            const maybe = n > 1e12 ? new Date(n) : new Date(n*1000);
            if (!Number.isNaN(maybe.getTime())) dateVal = maybe.toISOString().slice(0,10);
          } else {
            const parsed = new Date(d);
            if (!Number.isNaN(parsed.getTime())) dateVal = parsed.toISOString().slice(0,10);
            else dateVal = String(d);
          }
        }
      }

      setForm({
        title: initial.title || "",
        date: dateVal,
        startTime: sTime,
        endTime: eTime,
        location: initial.location || "",
        description: initial.description || "",
        category: initial.category || "",
        registrationLink: initial.registrationLink || "",
        featured: !!initial.featured,
        active: !!initial.active,
  image: null,
      });
      setLinkError("");
      setIsCheckingLink(false);
    } else {
  setForm({ title: "", date: "", startTime: "", endTime: "", location: "", description: "", category: "", registrationLink: "", featured: false, active: false, image: null });
      setLinkError("");
      setIsCheckingLink(false);
    }
  }, [initial, open]);

  if (!open) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
    if (name === 'registrationLink') setLinkError("");
  };

  const handleFile = (e) => {
    const image = e.target.files && e.target.files[0];
    setForm(f => ({ ...f, image }));
  };

  const isValidUrl = (value) => {
    if (!value) return true;
    try {
      const parsed = new URL(value.trim());
      return parsed.protocol === 'https:';
    } catch (err) {
      return false;
    }
  };

  const checkLinkExists = async (url) => {
    if (!url) return true;
    const target = url.trim();
    if (!target) return true;

    const attemptFetch = async (method) => {
      const response = await fetch(target, {
        method,
        mode: "no-cors",
      });
      if (response.ok) return true;
      if (response.type === "opaque") return true;
      return response.status >= 200 && response.status < 400;
    };

    try {
      if (await attemptFetch("HEAD")) return true;
    } catch (error) {
      // swallow and try GET
    }

    try {
      if (await attemptFetch("GET")) return true;
    } catch (error) {
      return false;
    }

    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving || isCheckingLink) return;
    if (!isValidUrl(form.registrationLink)) {
      setLinkError('Please provide a valid URL that starts with https://.');
      return;
    }
    if (form.registrationLink) {
      setIsCheckingLink(true);
      const exists = await checkLinkExists(form.registrationLink);
      setIsCheckingLink(false);
      if (!exists) {
        setLinkError('We could not reach this link. Please verify it exists and try again.');
        return;
      }
    }
    const fd = new FormData();
    fd.append('title', form.title || '');
    fd.append('description', form.description || '');
    // date to ISO when possible
    let dateVal = '';
    if (form.date instanceof Date) dateVal = form.date.toISOString();
    else if (typeof form.date === 'string' && form.date.trim()) {
      const s = form.date.trim();
      const isoCandidate = new Date(s + 'T00:00:00Z');
      if (!Number.isNaN(isoCandidate.getTime())) dateVal = isoCandidate.toISOString();
      else dateVal = s;
    }
    fd.append('date', dateVal);

    // Build startDateTime/endDateTime in local timezone with offset
    const pad = (n) => String(n).padStart(2, '0');
    const formatWithOffset = (d) => {
      if (!(d instanceof Date) || isNaN(d.getTime())) return '';
      const tzMin = -d.getTimezoneOffset();
      const sign = tzMin >= 0 ? '+' : '-';
      const absMin = Math.abs(tzMin);
      const offH = pad(Math.floor(absMin / 60));
      const offM = pad(absMin % 60);
      return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}${sign}${offH}:${offM}`;
    };

    let startDateTimeStr = '';
    let endDateTimeStr = '';
    try {
      if (form.date && form.startTime) {
        const [y, m, day] = form.date.split('-').map(Number);
        const [hh, mm] = (form.startTime || '00:00').split(':').map(Number);
        const sd = new Date(y, m - 1, day, hh || 0, mm || 0, 0);
        startDateTimeStr = formatWithOffset(sd);
      }
      if (form.date && form.endTime) {
        const [y, m, day] = form.date.split('-').map(Number);
        const [hh, mm] = (form.endTime || '00:00').split(':').map(Number);
        const ed = new Date(y, m - 1, day, hh || 0, mm || 0, 0);
        endDateTimeStr = formatWithOffset(ed);
      }
    } catch (err) {
      // ignore and fall back to empty strings
    }

    // include both canonical datetime fields and legacy time fields for compatibility
    fd.append('startDateTime', startDateTimeStr);
    fd.append('endDateTime', endDateTimeStr);
    fd.append('startTime', form.startTime || '');
    fd.append('endTime', form.endTime || '');
    fd.append('location', form.location || '');
    fd.append('category', form.category || '');
    fd.append('registrationLink', form.registrationLink || '');
    fd.append('featured', form.featured ? 'true':'false');
    fd.append('active', form.active ? 'true' : 'false');
  if (form.image) fd.append('image', form.image);
    onSave(fd);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start sm:items-center justify-center z-50 p-4 overflow-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 ring-1 ring-black/5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-xl font-semibold">{initial ? 'Edit Event' : 'Add Event'}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="text-gray-500 hover:text-gray-700 p-1">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Title:</label>
            <input
              type="text"
              name="title"
              value={form.title}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Date:</label>
            <input
              type="date"
              name="date"
              value={form.date || ""}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg p-2"
            />
          </div>

          <div className="mb-4 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Start time:</label>
              <input type="time" name="startTime" value={form.startTime} onChange={handleChange} className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-300" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">End time:</label>
              <input type="time" name="endTime" value={form.endTime} onChange={handleChange} className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-300" />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Location:</label>
            <input name="location" value={form.location} onChange={handleChange} className="w-full border border-gray-300 rounded-lg p-2" />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Category:</label>
            <input name="category" value={form.category} onChange={handleChange} className="w-full border border-gray-300 rounded-lg p-2" />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Registration Link:</label>
            <input type="url" name="registrationLink" value={form.registrationLink} onChange={handleChange} placeholder="https://" className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-300" />
            {linkError ? <p className="mt-1 text-sm text-red-600">{linkError}</p> : null}
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">image:</label>
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center px-3 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-700 cursor-pointer hover:bg-gray-50">
                <input type="file" name="image" onChange={handleFile} className="hidden" />
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16v-8m0 0L3 8m4 0l4-4m6 16v-8m0 0l4-4m-4 4l-4-4" />
                </svg>
                Choose file
              </label>
              <span className="text-sm text-gray-600">{form.image ? form.image.name : 'No file selected'}</span>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Description:</label>
            <textarea name="description" value={form.description} onChange={handleChange} className="w-full border border-gray-300 rounded-lg p-2" />
          </div>
          <div className="flex gap-4 items-center">
            <label className="flex items-center"><input type="checkbox" name="featured" checked={form.featured} onChange={handleChange} className="mr-2" /> Featured</label>
            <label className="flex items-center"><input type="checkbox" name="active" checked={form.active} onChange={handleChange} className="mr-2" /> Active</label>
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded-md text-sm text-gray-700 hover:bg-gray-50" disabled={isSaving}>Cancel</button>
            <button
              type="submit"
              className={`px-4 py-2 rounded-md text-sm text-white ${isSaving || isCheckingLink ? "bg-blue-300 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}
              disabled={isSaving || isCheckingLink}
            >
              {isCheckingLink ? 'Validating link...' : isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
